import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addToWhereClause,
  FlexRow,
  FormCol2,
  FormCol4,
  sqlDateTimeStr,
  sqlDateTimeUtcStr,
  toastFailure,
} from 'motion-components';
import { Card, Row, Spinner } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { Filters } from './filters';
import { PivotTableChart } from './components';

import gql from 'graphql-tag';

import { useApolloClient } from '@apollo/client';

import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isAfisCyprus, regionTitle } from 'shared-pages/common/constants';
import { endOfMonth, startOfMonth } from 'date-fns';
import { XAxisValues } from './fs-x-axis';
import { yAxisValues } from './fs-y-axis';

function sqlDatePeriodWhereClause(
  fieldName: string,
  startDT: Date,
  endDT: Date,
  runningAt: 'SERVER' | 'CLIENT' = 'SERVER',
): string {
  const datetimeToStrFunc = runningAt === 'SERVER' ? sqlDateTimeUtcStr : sqlDateTimeStr;
  return `((${fieldName} >= '${datetimeToStrFunc(
    startDT,
  )}') AND (${fieldName} <= '${datetimeToStrFunc(endDT)}'))`;
}

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const CollectionsRepDgrmPage = () => {
  const { appStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('CollectionsRepDgrmPage');

  const location = useLocation<any>();
  const history = useHistory();
  const { t } = useTranslation();
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  interface DataRec {
    xValueId: number;
    xValueName: string;
    yValue: number;
  }
  const [data, setData] = useState<Array<DataRec>>();
  const [currentFilters, setCurrentFilters] = useState<any>(null);

  // Helper to get display labels and measure metadata
  const getDisplayMetadata = () => {
    if (!currentFilters) return null;

    let xLabel = '';
    switch (currentFilters.xAxisId) {
      case XAxisValues.Materials:
        xLabel = t('Υλικό');
        break;
      case XAxisValues.Region:
        xLabel = t(regionTitle);
        break;
      case XAxisValues.Date:
        xLabel = t('Ημερομηνία');
        break;
      default:
        xLabel = t('Διάσταση');
    }

    let measure = { label: '', unit: undefined, digits: undefined };
    switch (currentFilters.yAxisId) {
      case yAxisValues.Weight:
        measure = {
          label: t('Βάρος'),
          unit: 'kg' as const,
          digits: 2,
        };
        break;
      case yAxisValues.CollectionsCount:
        measure = {
          label: t('Αριθμός Συλλογών'),
          unit: undefined,
          digits: 0,
        };
        break;
      default:
        measure = {
          label: t('Αξία'),
          unit: undefined,
          digits: 0,
        };
    }

    return { xLabel, measure };
  };

  const title = t('Ανάλυση Συλλογών');

  const composeSqlScript = async (values) => {
    // SELECT/JOIN mapping
    let selectClause = '';
    let joinClause = '';
    const orderByClause = 'ORDER BY yValue DESC';
    const groupByClause = 'GROUP BY xValueId, xValueName';

    if (values.xAxisId === XAxisValues.Materials) {
      selectClause = 'cl.materialId AS xValueId, m.name AS xValueName';
      joinClause = 'LEFT JOIN materials AS m ON m.id = cl.materialId';
    } else if (values.xAxisId === XAxisValues.Region) {
      selectClause = 'r.id AS xValueId, r.name AS xValueName';
      joinClause = `LEFT JOIN collectionpoints AS cp ON cp.id = c.collectionPointId
                    LEFT JOIN regions AS r ON r.id = cp.regionId`;
    } else if (values.xAxisId === XAxisValues.Date) {
      // Keep xValueId numeric if your AggregatedPoint demands it (e.g. UNIX day):
      // selectClause = "UNIX_TIMESTAMP(DATE(c.dateCollected)) AS xValueId, DATE_FORMAT(c.dateCollected,'%d/%m/%Y') AS xValueName";
      // If xValueId can be string, keep both date strings:
      selectClause =
        "DATE_FORMAT(c.dateCollected,'%d/%m/%Y') AS xValueId, DATE_FORMAT(c.dateCollected,'%d/%m/%Y') AS xValueName";
    }

    // Measure
    const rankField =
      values.yAxisId === yAxisValues.CollectionsCount
        ? 'COUNT(c.id) AS yValue'
        : 'SUM(cl.netWeight) AS yValue'; // use consistent casing

    // WHERE conditions
    const conditions: string[] = [];
    if (values.startDate || values.endDate) {
      conditions.push(
        sqlDatePeriodWhereClause('dateCollected', values.startDate, values.endDate, 'CLIENT'),
      );
    }
    if (values.collectorId) {
      conditions.push(`c.collectorId = ${Number(values.collectorId)}`);
    }
    if (values.wasteReceptionPointId) {
      conditions.push(`c.wasteReceptionPointId = ${Number(values.wasteReceptionPointId)}`);
    }
    if (values.regionId) {
      // requires join to regions via collectionpoints (already present for Region/Date axes)
      conditions.push(`cp.regionId = ${Number(values.regionId)}`);
    }

    const whereClause = conditions.length ? `WHERE ${conditions.join(' AND ')}` : '';

    return `
      SELECT
        ${selectClause},
        ${rankField}
      FROM collections AS c
      LEFT JOIN collections_lines AS cl ON cl.collectionId = c.id
      ${joinClause}
      ${whereClause}
      ${groupByClause}
      ${orderByClause}
      ;
    `;
  };

  /**
    Fetch Data
  */
  const filtersChanged = async (values) => {
    setLoading(true);
    setCurrentFilters(values);
    if (process.env.NODE_ENV !== 'production') {
      console.log('filters', values);
    }

    const script = await composeSqlScript(values);
    if (process.env.NODE_ENV !== 'production') {
      console.log(script);
    }

    const response = await client.query({
      query: gql`
        query sqlQuery($script: String) {
          sqlQuery(script: $script)
        }
      `,
      variables: {
        script: script,
      },
      fetchPolicy: 'network-only',
    });
    const sqlResponseStr = response?.data?.sqlQuery;
    const sqlResponse = JSON.parse(sqlResponseStr);
    if (process.env.NODE_ENV !== 'production') {
      console.log(sqlResponse);
    }

    setData(sqlResponse);
    setLoading(false);
  };

  /**
   * Render Component
   */
  const displayMeta = getDisplayMetadata();

  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{title}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        {/* Φίλτρα */}
        <Filters
          onChange={filtersChanged}
          initialValues={{
            xAxisId: isAfisCyprus ? XAxisValues.Region : XAxisValues.Materials,
            yAxisId: yAxisValues.Weight,
            startDate: startOfMonth(new Date()),
            endDate: endOfMonth(new Date()),
          }}
        />

        {/* Pivot Table + Chart */}
        <div className="pt-3 pb-3">
          {displayMeta ? (
            <PivotTableChart
              data={data || []}
              xLabel={displayMeta.xLabel}
              measure={displayMeta.measure}
              loading={loading}
              layout="side"
            />
          ) : (
            <div className="text-center text-muted py-4">
              {t('Επιλέξτε κριτήρια αναζήτησης για εμφάνιση των αποτελεσμάτων')}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};
